/* eslint-disable */
import React, { useMemo, useState } from 'react';
import { Box, Typography, Select, MenuItem, Skeleton } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { formatAmount } from 'Utils/ulilities';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const fmt = (v) => formatAmount(v || 0).trim();

const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

/* ── Point-halo plugin ───────────────────────────────────────── */
const pointHaloPlugin = {
  id: 'pointHalo',
  beforeDatasetsDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((_, i) => {
      const meta = chart.getDatasetMeta(i);
      if (meta.hidden) return;
      meta.data.forEach((point, j) => {
        if ((chart.data.datasets[i].data[j] || 0) === 0) return;
        ctx.save();
        ctx.beginPath();
        ctx.arc(point.x, point.y, 18, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,123,198,0.22)';
        ctx.fill();
        ctx.restore();
      });
    });
  },
};

/* ── Evolution chart ─────────────────────────────────────────── */
const EvolutionChart = ({ monthList = [], loading }) => {
  const [range, setRange] = useState(6);

  const { chartData, options } = useMemo(() => {
    const now = new Date();
    const grid = [];
    for (let i = range - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const match = monthList.find((m) => {
        const md = new Date(m.month);
        return md.getFullYear() === d.getFullYear() && md.getMonth() === d.getMonth();
      });
      grid.push({
        label: MONTH_LABELS[d.getMonth()],
        amount: match ? (match.amount || 0) : 0,
      });
    }

    const amounts = grid.map((g) => g.amount);
    const labels = grid.map((g) => g.label);
    const maxAmount = Math.max(...amounts, 0);
    const yMax = maxAmount > 0 ? Math.ceil((maxAmount * 1.15) / 2500) * 2500 : 12500;

    return {
      chartData: {
        labels,
        datasets: [
          {
            data: amounts,
            fill: true,
            clip: false,
            backgroundColor: (ctx) => {
              const chart = ctx.chart;
              const { ctx: c, chartArea } = chart;
              if (!chartArea) return '#007BC6';
              const gradient = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              gradient.addColorStop(0, 'rgba(0,123,198,0.65)');
              gradient.addColorStop(0.5, 'rgba(0,123,198,0.50)');
              gradient.addColorStop(1, 'rgba(0,123,198,0.00)');
              return gradient;
            },
            borderColor: 'rgba(0,123,198,0.68)',
            borderWidth: 1.5,
            tension: 0.45,
            pointBackgroundColor: '#007BC6',
            pointBorderColor: '#fff',
            pointBorderWidth: 1.5,
            pointRadius: (ctx) => ctx.raw === 0 ? 2 : 5,
            pointHoverRadius: 7,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#fff',
            borderColor: '#E8ECF0',
            borderWidth: 1,
            titleColor: '#4D4D4D',
            bodyColor: '#007BC6',
            padding: 10,
            callbacks: {
              label: (ctx) => ` ${fmt(ctx.raw)} DHS`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false, drawBorder: false },
            border: { display: false, width: 0 },
            ticks: { color: '#A0AEC0', font: { size: 11, family: 'Open Sans', weight: '600' } },
          },
          y: {
            min: 0,
            max: yMax,
            grid: { color: '#F0F2F5', drawTicks: false, drawBorder: false },
            border: { display: false, width: 0 },
            ticks: {
              color: '#A0AEC0',
              font: { size: 9, family: 'Open Sans', weight: '600' },
              stepSize: yMax / 5,
              padding: 12,
              callback: (v) => (v === 0 ? '0' : `${v / 1000}k`),
            },
          },
        },
      },
    };
  }, [monthList, range]);

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #D1D5DC',
        borderRadius: '12px',
        boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.06)',
        p: '20px 24px',
        mt: '20px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '20px' }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#717182', letterSpacing: '0.5px' }}>
          EVOLUTION DES ACHATS
        </Typography>
        <Select
          value={range}
          onChange={(e) => setRange(e.target.value)}
          size="small"
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#007BC6',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiSelect-icon': { color: '#007BC6' },
          }}
        >
          <MenuItem value={3} sx={{ fontSize: 12 }}>3 DERNIERS MOIS</MenuItem>
          <MenuItem value={6} sx={{ fontSize: 12 }}>6 DERNIERS MOIS</MenuItem>
          <MenuItem value={12} sx={{ fontSize: 12 }}>12 DERNIERS MOIS</MenuItem>
        </Select>
      </Box>
      <Box sx={{ height: '280px' }}>
        {loading
          ? <Skeleton variant="rectangular" height={280} sx={{ borderRadius: '8px' }} />
          : <Line key={range} plugins={[pointHaloPlugin]} data={chartData} options={options} />}
      </Box>
    </Box>
  );
};

export default EvolutionChart;
